/**
 * @file useContractExtractionEditMode.ts
 * @description Hook that manages the SAVED-extraction edit-mode lifecycle
 *   for the contract drawer (SE-4347 Story 3.2 AC-2 / AC-3).
 * @module features/contracts/hooks/useContractExtractionEditMode
 */

import { useCallback, useMemo, useState } from "react";

/**
 * Public API returned by {@link useContractExtractionEditMode}.
 * @remarks The hook tracks three pieces of state:
 * - `editMode` — whether the user is currently editing a SAVED extraction
 * - `isDiscardDialogOpen` — whether the "Discard unsaved changes?" AlertDialog is open
 * - the snapshot of `extractedData` taken when edit mode was entered,
 *   which is compared against the live data to derive `isDirty`
 */
export interface UseContractExtractionEditModeResult {
  /** Whether the drawer is currently in edit mode. */
  readonly editMode: boolean;
  /**
   * Whether the user has made any change since entering edit mode.
   * Computed by reference-and-structural-equality comparison between
   * the snapshot taken on entry and the current `extractedData`.
   */
  readonly isDirty: boolean;
  /** Whether the "Discard unsaved changes?" dialog is visible. */
  readonly isDiscardDialogOpen: boolean;
  /**
   * Enters edit mode and snapshots the current `extractedData` so a
   * subsequent `confirmDiscard` can compare against it.
   */
  readonly enterEditMode: () => void;
  /**
   * Imperatively exits edit mode and clears the snapshot. Used by the
   * caller when an out-of-band signal (e.g. extraction status flipping
   * to SAVED after a successful Save) implies the local edit-mode
   * overlay should close. Does NOT open the discard dialog or invoke
   * `onDiscard` — there's nothing to discard once the server already
   * accepted the edit.
   */
  readonly exitEditMode: () => void;
  /**
   * Routes the Cancel button:
   * - when `isDirty` is true, opens the discard-edits dialog
   * - otherwise exits edit mode immediately
   */
  readonly requestCancel: () => void;
  /** Closes the discard-edits dialog without discarding. */
  readonly dismissDiscardDialog: () => void;
  /**
   * Confirms the discard: clears the dialog, exits edit mode, and
   * triggers the caller-provided `onDiscard` side effect so the caller
   * can refetch or locally reset extraction data.
   */
  readonly confirmDiscard: () => void;
}

/**
 * Compares two `extractedData` values by reference first (a cheap guard
 * for same-instance or primitive equality), then by JSON serialization.
 * Because edit-mode snapshotting deep-clones `extractedData` on entry,
 * the snapshot and the live value are always distinct object references
 * while editing — JSON serialization is therefore the operative
 * comparison path in practice. The reference check is retained as a
 * fast-path guard for out-of-edit-mode callers and for primitive values
 * such as `null`.
 * @param a - First value to compare
 * @param b - Second value to compare
 * @returns Whether the two values are structurally equal
 * @remarks JSON.stringify is order-sensitive; since the backend returns
 *   category keys in a stable order and the inline edit UI mutates items
 *   in place, the ordering is preserved — which matches the user's mental
 *   model of "changed vs unchanged" well.
 */
function isStructurallyEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

/**
 * Deep-clones an `extractedData` map so the edit-mode snapshot shares
 * no nested references with the live Apollo-provided value.
 *
 * Prefers `structuredClone` (available in every runtime we target —
 * web, Hermes 0.73+, Node 17+); falls back to JSON round-trip for
 * older environments. The JSON fallback is sufficient here because the
 * extraction payload is a plain category → line-item-array map with no
 * functions, Dates, Maps, or cyclic refs — the backend serialises it
 * the same way.
 * @param value - The extractedData object to clone
 * @returns A deeply independent copy of `value`
 */
function deepCloneExtractedData(
  value: Record<string, unknown>
): Record<string, unknown> {
  if (typeof globalThis.structuredClone === "function") {
    return globalThis.structuredClone(value) as Record<string, unknown>;
  }
  return JSON.parse(JSON.stringify(value)) as Record<string, unknown>;
}

/**
 * Optional inputs to {@link useContractExtractionEditMode}.
 */
export interface UseContractExtractionEditModeOptions {
  /**
   * UI-layer dirtiness signal — `true` when any inline-edit surface (a
   * NEW unsaved line-item row, or an existing row whose inline draft
   * diverges from the saved text) holds pending keystrokes that the
   * server-side `extractedData` has not yet absorbed (no mutation has
   * fired). When true, the hook's `isDirty` is forced to `true` so
   * Cancel routes through the DiscardEditsDialog instead of silently
   * throwing the typing away. SE-4347 AC-3 — Option A.
   *
   * Folded in ONLY while `editMode === true` so a stray `true` outside
   * edit mode cannot falsely open the dialog.
   */
  readonly hasUnsavedInlineEdits?: boolean;
}

/**
 * Hook that owns the edit-mode state machine for the contract drawer.
 * @param extractedData - The currently-rendered extracted data. Used to snapshot on entry and detect dirtiness.
 * @param onDiscard - Optional side effect invoked after a confirmed discard. Receives the deep-cloned `extractedData` snapshot taken at `enterEditMode` so the caller can pass it to a server-side revert mutation (SE-4768) before refetching. The snapshot is delivered AFTER the local edit-mode state has been reset, so callers don't need to coordinate with the hook's own state.
 * @param options - Optional extras. See {@link UseContractExtractionEditModeOptions}.
 * @returns The hook's public API
 * @remarks
 * - `isDirty` is computed from the snapshot captured by `enterEditMode`
 *   and the latest `extractedData` — callers do not need to pass diffs.
 * - The hook does not hit the network. All server-side reverts happen
 *   via the caller's `onDiscard` callback. Pre-SE-4768 callers wired
 *   this to a bare refetch (which silently failed because line-item
 *   mutations persist immediately and refetch returns the already-
 *   mutated state); post-SE-4768 callers receive the entry snapshot
 *   as the callback's first argument and pass it to
 *   `revertContractExtraction` to actually undo persisted edits.
 * - The snapshot is stored in state (not a ref) so `isDirty` can be
 *   derived during render without tripping the `react-hooks/refs`
 *   linter. Only `enterEditMode` and `exitEditMode` ever mutate it,
 *   both via `useState` setters outside render.
 * - `options.hasUnsavedInlineEdits` folds UI-layer dirtiness (e.g. a
 *   newly-typed unsaved line item) into `isDirty` so Cancel opens the
 *   DiscardEditsDialog instead of silently dropping keystrokes.
 */
export function useContractExtractionEditMode(
  extractedData: Record<string, unknown>,
  onDiscard?: (snapshotData: Record<string, unknown>) => void,
  options?: UseContractExtractionEditModeOptions
): UseContractExtractionEditModeResult {
  const hasUnsavedInlineEdits = options?.hasUnsavedInlineEdits ?? false;
  const [editMode, setEditMode] = useState(false);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [snapshot, setSnapshot] = useState<Record<string, unknown> | null>(
    null
  );

  const isDirty = useMemo(() => {
    if (!editMode || snapshot === null) return false;
    if (hasUnsavedInlineEdits) return true;
    return !isStructurallyEqual(snapshot, extractedData);
  }, [editMode, snapshot, extractedData, hasUnsavedInlineEdits]);

  const enterEditMode = useCallback(() => {
    // Deep-clone the nested category/item structure so that any in-place
    // mutation of `extractedData` (e.g. optimistic Apollo cache writes
    // that reuse nested object identities) cannot also mutate `snapshot`
    // and silently flip `isDirty` to `false`. structuredClone is
    // available in all of the runtimes we target (web, iOS/Android
    // Hermes 0.73+, Node 17+); the JSON fallback keeps older environments
    // working even though the structural-equal comparator serialises on
    // read anyway.
    setSnapshot(deepCloneExtractedData(extractedData));
    setEditMode(true);
  }, [extractedData]);

  const exitEditMode = useCallback(() => {
    setSnapshot(null);
    setEditMode(false);
  }, []);

  const requestCancel = useCallback(() => {
    if (!editMode) return;
    const serverDirty =
      snapshot !== null && !isStructurallyEqual(snapshot, extractedData);
    const dirty = serverDirty || hasUnsavedInlineEdits;
    if (dirty) {
      setIsDiscardDialogOpen(true);
      return;
    }
    exitEditMode();
  }, [editMode, snapshot, extractedData, hasUnsavedInlineEdits, exitEditMode]);

  const dismissDiscardDialog = useCallback(() => {
    setIsDiscardDialogOpen(false);
  }, []);

  const confirmDiscard = useCallback(() => {
    // Capture the snapshot BEFORE `exitEditMode` clears it so we can
    // hand it to the caller's onDiscard. The snapshot is the
    // deep-clone taken at `enterEditMode` (see deepCloneExtractedData
    // there), so it's safe to ship across the wire. SE-4768 wires this
    // payload into `revertContractExtraction` so persisted line-item
    // edits are actually undone server-side.
    //
    // Defensive fallback: if confirmDiscard somehow runs without a
    // snapshot (no enterEditMode preceded it), invoke onDiscard with
    // an empty object so callers don't have to null-check. The hook's
    // internal state machine should make this unreachable in practice.
    const snapshotForDiscard = snapshot ?? {};
    setIsDiscardDialogOpen(false);
    exitEditMode();
    if (onDiscard) onDiscard(snapshotForDiscard);
  }, [exitEditMode, onDiscard, snapshot]);

  return useMemo(
    () => ({
      editMode,
      isDirty,
      isDiscardDialogOpen,
      enterEditMode,
      exitEditMode,
      requestCancel,
      dismissDiscardDialog,
      confirmDiscard,
    }),
    [
      editMode,
      isDirty,
      isDiscardDialogOpen,
      enterEditMode,
      exitEditMode,
      requestCancel,
      dismissDiscardDialog,
      confirmDiscard,
    ]
  );
}
